import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, hasSession, setSessionExpiredHandler } from '../api/client';
import type { Me } from '@pixerito/api-client';

interface AuthState {
  me: Me | null;
  ready: boolean;
  login: (email: string, password: string) => Promise<Me>;
  logout: () => void;
  /** Relê o usuário (ex.: depois de trocar a senha). */
  reload: () => Promise<Me>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(!hasSession());

  const logout = useCallback(() => {
    api.logout();
    setMe(null);
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(() => setMe(null));
    if (!hasSession()) return;
    api
      .me()
      .then(setMe)
      .catch(() => api.logout())
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await api.login(email, password);
    const user = await api.me();
    setMe(user);
    return user;
  }, []);

  const reload = useCallback(async () => {
    const user = await api.me();
    setMe(user);
    return user;
  }, []);

  const value = useMemo(() => ({ me, ready, login, logout, reload }), [me, ready, login, logout, reload]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fora do AuthProvider');
  return ctx;
}
